import { Centrifuge, UnauthorizedError, type Subscription } from 'centrifuge';
import type { RealtimeState, RealtimeTransport, SubscriptionHandlers } from '@/lib/realtime';

/**
 * The ONLY file that imports the Centrifugo SDK (design 4.1). A RealtimeTransport over one `Centrifuge`
 * client: the connection token comes from `getToken` (the session JWT), subscriptions carry no token
 * (the subscribe proxy authorizes them; a refusal ends that one subscription, never the connection).
 * Proven in the smoke (W7), not in unit tests: the contract it implements is what the tests pin.
 */
export function createCentrifugeTransport(endpoint: string): RealtimeTransport {
  let client: Centrifuge | null = null;
  const stateListeners = new Set<(s: RealtimeState) => void>();
  const channels = new Map<string, SubscriptionHandlers>();
  const subs = new Map<string, Subscription>();
  const emit = (s: RealtimeState) => stateListeners.forEach((cb) => cb(s));

  const attach = (c: Centrifuge, channel: string, h: SubscriptionHandlers) => {
    const sub = c.getSubscription(channel) ?? c.newSubscription(channel);
    sub.on('publication', (ctx) => h.onPublication(ctx.data));
    sub.on('subscribed', (ctx) => h.onSubscribed({ wasRecovering: ctx.wasRecovering, recovered: ctx.recovered }));
    sub.on('error', (ctx) => {
      console.warn('realtime: subscription error', channel, ctx.error?.message);
      h.onError();
    });
    // A proxy refusal (403) unsubscribes the channel server side: that channel only. Code 0 is our own
    // unsubscribe.
    sub.on('unsubscribed', (ctx) => {
      if (ctx.code !== 0) h.onError();
    });
    sub.subscribe();
    subs.set(channel, sub);
  };

  return {
    connect(getToken) {
      if (client) client.disconnect();
      const c = new Centrifuge(endpoint, {
        getToken: async () => {
          try {
            return await getToken();
          } catch {
            // No session: stop reconnecting until a new connect.
            throw new UnauthorizedError('no session');
          }
        },
      });
      c.on('connecting', () => emit('connecting'));
      c.on('connected', () => emit('connected'));
      c.on('disconnected', () => emit('disconnected'));
      client = c;
      for (const [channel, h] of channels) attach(c, channel, h);
      c.connect();
    },
    disconnect() {
      if (!client) return;
      for (const sub of subs.values()) client.removeSubscription(sub);
      subs.clear();
      client.disconnect();
      client = null;
      emit('disconnected');
    },
    onState(cb) {
      stateListeners.add(cb);
      return () => stateListeners.delete(cb);
    },
    subscribe(channel, h) {
      channels.set(channel, h);
      if (client) attach(client, channel, h);
      return () => {
        channels.delete(channel);
        const sub = subs.get(channel);
        subs.delete(channel);
        if (sub && client) client.removeSubscription(sub);
      };
    },
  };
}
